import { useMemo } from "react"
import { DateTime } from "luxon"
import {
    Box,
    FormControlLabel,
    FormHelperText,
    Radio,
    RadioGroup,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLock } from "@fortawesome/pro-solid-svg-icons"

import { selectSelectedLocale } from "../../../../../../modules/localization/localizationSelectors"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { FormikDatePicker, FormikNumberField, FormikTextField } from "../../../../forms/Formik"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { StandardButton } from "../../../../buttons/StandardButton"

import { clampRepeatLimits, getRepeatDeliveryDays, MAX_REPEAT_COUNT, MAX_REPEAT_INTERVAL } from "./helpers"
import { RepeatDeliveryDays } from "./RepeatDeliveryDays"
import { RepeatOrderFormValues } from "./RepeatOrderDialog"

const useStyles = makeStyles()((theme) => ({
    numberField: {
        width: "4rem",
    },
    radioGroup: {
        gap: ".5rem",
    },
    helperTextDisabled: {
        color: theme.palette.tailwind.slate[500],
    },
    helperText: {
        color: theme.palette.tailwind.slate[800],
    },
    toggleButtonGroup: {
        gap: ".5rem",
    },
    toggleButton: {
        textAlign: "center",
        fontWeight: "400",
        textTransform: "capitalize",
        width: "2.5rem",
        height: "2.5rem",
        padding: "0",
        color: theme.palette.tailwind.slate[800],
        backgroundColor: theme.palette.common.white,
        borderColor: theme.palette.tailwind.slate[300],
        borderRadius: "50%",
        "&.Mui-selected": {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.tailwind.blue[500],
        },
        "&.MuiToggleButtonGroup-grouped:not(:first-of-type), &.MuiToggleButtonGroup-grouped:not(:last-of-type)": {
            borderRadius: "50%",
            borderColor: theme.palette.tailwind.slate[300],
        },
    },
    buttonWrap: {
        textAlign: "center",
        paddingTop: "1.5rem",
    },
    button: {
        minWidth: "15rem",
    },
    box: {
        display: "grid",
        gridTemplateColumns: "1fr 2fr",
        gap: "1rem",
    },
    lockedDateField: {
        "& .MuiInputBase-root": {
            backgroundColor: theme.palette.tailwind.slate[50],
            cursor: "help",
        },
        "& .MuiInputBase-input": {
            cursor: "help",
        },
    },
    startDateHelp: {
        color: theme.palette.tailwind.slate[500],
    },
}))

function LockedDateIcon() {
    return <FontAwesomeIcon icon={faLock} size="xs" />
}

export function RepeatOrderDialogForm() {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const locale = useAppSelector(selectSelectedLocale)
    const { values, setFieldValue, setValues, errors } = useFormikContext<RepeatOrderFormValues>()
    const days = useMemo(() => getRepeatDeliveryDays(clampRepeatLimits(values)), [values])
    const deliveryCount = days.filter((day) => !day.isDeselected).length

    // `value` stays a stable English weekday name (consumed downstream for RRule mapping),
    // while `label` is the localized short name shown on the toggle button.
    const weekDayOptions = useMemo(
        () =>
            Array.from({ length: 7 }, (_, i) => {
                const dayDate = DateTime.local().set({ weekday: i + 1 })
                return {
                    value: dayDate.toFormat("EEEE", { locale: "en-US" }),
                    label: dayDate.setLocale(locale).toLocaleString({ weekday: "short" }),
                }
            }),
        [locale],
    )

    const handleRepeatTypeChange = (repeatType: string) => {
        const startWeekDay = values.startDate?.toFormat("EEEE", { locale: "en-US" })

        void setValues({
            ...values,
            repeatType,
            weekDays:
                repeatType === "week" && !values.weekDays.length && startWeekDay ? [startWeekDay] : values.weekDays,
        })
    }

    return (
        <Stack gap="2.5rem">
            <Stack gap="1.5rem">
                <Stack gap=".5rem">
                    <LocalizedStrict id="create-order-drawer-repeat-order-every">
                        <Typography variant="subtitle1">Repeat every:</Typography>
                    </LocalizedStrict>
                    <Stack flexDirection="row" gap="1rem" alignItems="center">
                        <FormikNumberField
                            name="repeatInterval"
                            min={1}
                            max={MAX_REPEAT_INTERVAL}
                            integerOnly
                            stepper={false}
                            size="small"
                            className={classes.numberField}
                        />
                        <RadioGroup
                            row
                            value={values.repeatType}
                            onChange={(e) => handleRepeatTypeChange(e.target.value)}
                        >
                            <LocalizedStrict id="create-order-drawer-repeat-order-every-day" attrs={{ label: true }}>
                                <FormControlLabel value="day" control={<Radio size="small" />} label="Day" />
                            </LocalizedStrict>
                            <LocalizedStrict id="create-order-drawer-repeat-order-every-week" attrs={{ label: true }}>
                                <FormControlLabel value="week" control={<Radio size="small" />} label="Week" />
                            </LocalizedStrict>
                        </RadioGroup>
                    </Stack>
                </Stack>
                {values.repeatType === "day" && (
                    <Stack gap=".5rem">
                        <LocalizedStrict id="create-order-drawer-repeat-order-applies-to">
                            <Typography variant="subtitle1">Applies to:</Typography>
                        </LocalizedStrict>
                        <RadioGroup
                            row
                            value={values.dayMode}
                            onChange={(e) => setFieldValue("dayMode", e.target.value)}
                        >
                            <LocalizedStrict
                                id="create-order-drawer-repeat-order-applies-to-weekdays"
                                attrs={{ label: true }}
                            >
                                <FormControlLabel
                                    value="weekdays"
                                    control={<Radio size="small" />}
                                    label="Weekdays (Mon–Fri)"
                                />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="create-order-drawer-repeat-order-applies-to-every-day"
                                attrs={{ label: true }}
                            >
                                <FormControlLabel value="all" control={<Radio size="small" />} label="Every day" />
                            </LocalizedStrict>
                        </RadioGroup>
                    </Stack>
                )}
                {values.repeatType === "week" && (
                    <Stack gap=".5rem">
                        <LocalizedStrict id="create-order-drawer-repeat-order-on">
                            <Typography variant="subtitle1">Repeat on:</Typography>
                        </LocalizedStrict>
                        <ToggleButtonGroup
                            value={values.weekDays || []}
                            onChange={(_, newValue) => setFieldValue("weekDays", newValue)}
                            className={classes.toggleButtonGroup}
                        >
                            {weekDayOptions.map(({ value, label }) => (
                                <ToggleButton key={value} value={value} className={classes.toggleButton}>
                                    {label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        {errors.weekDays && <FormHelperText error>{errors.weekDays}</FormHelperText>}
                    </Stack>
                )}
                <Stack
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: "1.5rem", sm: "5.5rem" }}
                    justifyContent="space-between"
                >
                    <Stack gap=".5rem">
                        <LocalizedStrict id="create-order-drawer-repeat-order-starts">
                            <Typography variant="subtitle1">Starts:</Typography>
                        </LocalizedStrict>
                        {/* zero delay: a tap on a phone opens it; long leave: time to read it */}
                        <Tooltip
                            title={l10n.getString("create-order-drawer-repeat-order-start-date-locked-message")}
                            placement="bottom-start"
                            enterTouchDelay={0}
                            leaveTouchDelay={6000}
                        >
                            <Box>
                                {/* "all": staff can set a past delivery date */}
                                <FormikDatePicker
                                    name="startDate"
                                    range="all"
                                    readOnly
                                    slots={{ openPickerIcon: LockedDateIcon }}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            className: classes.lockedDateField,
                                        },
                                    }}
                                />
                            </Box>
                        </Tooltip>
                        <LocalizedStrict id="create-order-drawer-repeat-order-start-date-help">
                            <Typography variant="caption" className={classes.startDateHelp}>
                                The order’s delivery date – cannot be changed
                            </Typography>
                        </LocalizedStrict>
                    </Stack>
                    <Stack gap=".5rem">
                        <LocalizedStrict id="create-order-drawer-repeat-order-ends">
                            <Typography variant="subtitle1">Ends:</Typography>
                        </LocalizedStrict>
                        <RadioGroup
                            value={values.endType}
                            onChange={(e) => setFieldValue("endType", e.target.value)}
                            className={classes.radioGroup}
                        >
                            <Box className={classes.box}>
                                <LocalizedStrict id="create-order-drawer-repeat-order-ends-on" attrs={{ label: true }}>
                                    <FormControlLabel value="on" control={<Radio size="small" />} label="On" />
                                </LocalizedStrict>
                                <FormikDatePicker
                                    name="endDate"
                                    range="now-to-future"
                                    disabled={values.endType !== "on"}
                                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                                    minDate={values.startDate ?? undefined}
                                />
                            </Box>
                            <Box className={classes.box}>
                                <LocalizedStrict
                                    id="create-order-drawer-repeat-order-ends-after"
                                    attrs={{ label: true }}
                                >
                                    <FormControlLabel value="after" control={<Radio size="small" />} label="After" />
                                </LocalizedStrict>
                                <Stack flexDirection="row" alignItems="center" gap=".5rem">
                                    <FormikNumberField
                                        name="repeatCount"
                                        fullWidth
                                        size="small"
                                        min={1}
                                        max={MAX_REPEAT_COUNT}
                                        integerOnly
                                        stepper={false}
                                        disabled={values.endType !== "after"}
                                        className={classes.numberField}
                                    />
                                    <LocalizedStrict id="create-order-drawer-repeat-order-ends-after-deliveries">
                                        <Typography
                                            className={cx(
                                                values.endType !== "after"
                                                    ? classes.helperTextDisabled
                                                    : classes.helperText,
                                            )}
                                        >
                                            deliveries
                                        </Typography>
                                    </LocalizedStrict>
                                </Stack>
                            </Box>
                        </RadioGroup>
                    </Stack>
                </Stack>
                <RepeatDeliveryDays days={days} />
            </Stack>

            <Box className={classes.buttonWrap}>
                <LocalizedStrict
                    id={
                        deliveryCount > 0
                            ? "create-order-drawer-repeat-order-confirm-orders-button"
                            : "create-order-drawer-repeat-order-confirm-button"
                    }
                    vars={{ count: deliveryCount }}
                >
                    <StandardButton
                        fullWidth={false}
                        updateDefaultClasses={classes.button}
                        type="submit"
                        disabled={deliveryCount === 0}
                    >
                        Confirm
                    </StandardButton>
                </LocalizedStrict>
            </Box>
        </Stack>
    )
}
